import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6a2brghk82orf0cjpg";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta(
    "联系反馈 - 自慰健康生活资讯",
    "如果你有内容建议、问题反馈或合作意向，欢迎通过本站联系反馈表单与我们取得联系，我们会认真阅读每一条信息。",
    "联系反馈,自慰,健康生活,内容建议",
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 反垃圾：蜜罐字段被填写则视为机器人，返回通用反馈但不发送数据
    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写姓名、邮箱和反馈内容。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请输入有效的邮箱地址。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg: string =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;

      if (response.ok && code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
      } else if (String(serverMsg).toLowerCase().includes("spam")) {
        setStatus("error");
        setFormError(serverMsg || "提交未通过校验，请检查内容后重试。");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <Breadcrumb items={[{ label: "联系反馈" }]} />
          <h1 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">联系反馈</h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl">
            对内容有建议、想指出错误，或有合作意向？欢迎留言，我们会认真阅读并尽量回复。
          </p>
        </div>
      </div>

      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* 表单 */}
          <div className="lg:col-span-2">
            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">给我们留言</h2>
              <p className="text-sm text-foreground-500 mb-6">带 * 的为必填项，反馈内容请控制在 500 字以内。</p>

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
                noValidate
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-800 mb-2">
                      姓名 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      placeholder="怎么称呼你"
                      className="w-full h-11 px-4 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-800 mb-2">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      className="w-full h-11 px-4 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-800 mb-2">
                    反馈内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={6}
                    maxLength={500}
                    placeholder="请描述你的建议、问题或反馈…"
                    className="w-full px-4 py-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                  ></textarea>
                </div>

                {/* 反垃圾蜜罐字段（请勿填写） */}
                <input
                  type="text"
                  name="contact_alt"
                  className="contact-note-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        提交反馈
                      </>
                    )}
                  </button>
                  {status === "success" && (
                    <span className="flex items-center gap-2 text-sm text-primary-700">
                      <i className="ri-checkbox-circle-line"></i>
                      提交成功，感谢你的反馈，我们会尽快阅读。
                    </span>
                  )}
                  {status === "error" && formError && (
                    <span className="flex items-center gap-2 text-sm text-accent-700">
                      <i className="ri-error-warning-line"></i>
                      {formError}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* 联系方式 */}
          <aside className="lg:col-span-1">
            <div className="rounded-lg bg-background-100 border border-background-200 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">其他联系方式</h2>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="text-foreground-900">邮箱</p>
                    <p className="text-foreground-600">hello@selfcare-info.example</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 shrink-0">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <p className="text-foreground-900">回复时间</p>
                    <p className="text-foreground-600">工作日 2-3 个工作日内</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 shrink-0">
                    <i className="ri-shield-check-line"></i>
                  </span>
                  <div>
                    <p className="text-foreground-900">隐私承诺</p>
                    <p className="text-foreground-600">信息仅用于回复你的反馈</p>
                  </div>
                </li>
              </ul>
              <p className="mt-5 text-xs text-foreground-500 leading-relaxed">
                说明：以上联系方式与回复时间为演示信息，仅用于展示页面效果。本站为健康科普资讯平台，不提供医疗服务。
              </p>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
              >
                了解更多关于我们
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}